import React, { useState, useRef, useMemo, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { useIntl } from 'react-intl';
import debounce from 'lodash.debounce';

import ColorButton from '@/components/common/elements/ColorButton';
import Modal from '@/components/common/Modal';
import EditIcon from '@/svg-icons/edit';

import { canvasPreview } from './canvasPreview';
import 'react-image-crop/dist/ReactCrop.css';
import classes from './style.module.scss';


function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: 'px',
        width: 200,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCrop = ({ onConfirm, maxWidth = 400, maxHeight = 400, aspect = 1 }) => {
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [modalVisible, setIsModalVisible] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();

  const intl = useIntl();

  const onSelectFile = React.useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result.toString() || ''));
      reader.readAsDataURL(e.target.files[0]);
    }
  }, []);

  const onImageLoad = React.useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  }, [aspect]);

  const debouncedSetCompletedCrop = useMemo(
    () => debounce(c => setCompletedCrop(c), 150),
    []
  );

  useEffect(() => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop]);

  const confirm = React.useCallback(async () => {
    const canvas = previewCanvasRef.current;

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/png');
    });

    const avatar = new File([blob], 'avatar.png', {
      type: 'image/png',
      lastModified: new Date().getTime(),
    });

    onConfirm(avatar);
    setIsModalVisible(false);
  }, [onConfirm]);

  const openModal = React.useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const closeModal = React.useCallback(() => {
    setIsModalVisible(false);
    setImgSrc(null);
    setCrop(undefined);
    setCompletedCrop();
  }, []);

  return (
    <div className={classes.flex}>
      <div className={classes.icon} onClick={openModal}>
        <EditIcon style={{ fontSize: '16px' }} />
      </div>

      {modalVisible && (
        <Modal className="w-fit min-w-[480px] max-w-[80vw]" onClose={closeModal}>
          <div className={classes.preview}>
            {completedCrop && (
              <canvas
                ref={previewCanvasRef}
                style={{
                  marginRight: '10px',
                  border: '1px solid black',
                  objectFit: 'contain',
                  width: 80,
                  height: 80,
                }}
              />
            )}
            <div className="w-full">
              <input type="file" accept="image/*" onChange={onSelectFile} className={classes.fileInput} />
            </div>
          </div>

          {imgSrc && (
            <>
              <div className="mb-2">
                <ReactCrop
                  className={classes.imageWrapper}
                  minWidth={200}
                  minHeight={200}
                  maxWidth={maxWidth}
                  maxHeight={maxHeight}
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={debouncedSetCompletedCrop}
                  aspect={aspect}
                >
                  <img ref={imgRef} alt="" src={imgSrc} onLoad={onImageLoad} />
                </ReactCrop>
              </div>
              <div className="text-center">
                <ColorButton onClick={closeModal}>{intl.formatMessage({ id: 'BUTTON.CANCEL' })}</ColorButton>
                <ColorButton color="green" onClick={confirm}>
                  {intl.formatMessage({ id: 'BUTTON.CONFIRM' })}
                </ColorButton>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ImageCrop;
